import { cn } from '@/lib/utils';

interface SkipNavProps {
  /** The ID of the main content element to skip to */
  mainContentId?: string;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Skip navigation link for keyboard and screen reader users.
 * Allows users to skip directly to main content.
 */
export function SkipNav({
  mainContentId = 'main-content',
  className,
}: SkipNavProps) {
  return (
    <a
      href={`#${mainContentId}`}
      className={cn(
        // Hidden by default, visible on focus
        'sr-only focus:not-sr-only',
        // Position when visible
        'focus:absolute focus:top-4 focus:left-4 focus:z-[100]',
        // Visual styling
        'focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground',
        'focus:rounded-md focus:shadow-lg',
        // Focus ring
        'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
        className
      )}
    >
      Skip to main content
    </a>
  );
}

/**
 * Multiple skip links for complex layouts
 */
interface SkipLink {
  label: string;
  targetId: string;
}

interface SkipNavMultipleProps {
  links: SkipLink[];
  className?: string;
}

export function SkipNavMultiple({ links, className }: SkipNavMultipleProps) {
  if (links.length === 0) return null;

  return (
    <div
      className={cn(
        'sr-only focus-within:not-sr-only',
        'focus-within:absolute focus-within:top-4 focus-within:left-4 focus-within:z-[100]',
        'focus-within:flex focus-within:flex-col focus-within:gap-2',
        'focus-within:p-2 focus-within:bg-background focus-within:rounded-lg focus-within:shadow-lg focus-within:border',
        className
      )}
    >
      {links.map((link) => (
        <a
          key={link.targetId}
          href={`#${link.targetId}`}
          className={cn(
            'px-3 py-1.5 text-sm font-medium rounded-md',
            'bg-primary text-primary-foreground',
            'hover:bg-primary/90',
            'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2'
          )}
        >
          {link.label}
        </a>
      ))}
    </div>
  );
}
